/**
 * Article cover image uploader – X-style: 5:2 recommended, S3 upload.
 */

import {useCallback, useRef, useState} from 'react'
import {Image, Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {launchImageLibraryAsync} from 'expo-image-picker'

import {uploadArticleCoverToS3} from '#/lib/api/studioArticleCover'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Camera_Stroke2_Corner0_Rounded as CameraIcon} from '#/components/icons/Camera'
import {TimesLarge_Stroke2_Corner0_Rounded as RemoveIcon} from '#/components/icons/Times'
import {Text} from '#/components/Typography'
import * as Toast from '#/components/Toast'
import {IS_WEB} from '#/env'

type Props = {
  coverImagePath: string | null
  onCoverChange: (url: string | null) => void
  disabled?: boolean
}

export function ArticleCoverImageUploader({
  coverImagePath,
  onCoverChange,
  disabled = false,
}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const coverImageUrl =
    coverImagePath?.startsWith('http') === true
      ? coverImagePath
      : coverImagePath
        ? `https://shxre.social${coverImagePath.startsWith('/') ? '' : '/'}${coverImagePath}`
        : null

  const pickAndUpload = useCallback(
    async (input: {type: 'file'; file: File} | {type: 'uri'; uri: string; mimeType?: string}) => {
      if (uploading || disabled) return
      setUploading(true)
      try {
        const url = await uploadArticleCoverToS3(
          input.type === 'file'
            ? input
            : {type: 'uri', uri: input.uri, mimeType: input.mimeType},
        )
        onCoverChange(url)
        Toast.show(_(msg`Cover image added`), 'check')
      } catch (e) {
        const message = e instanceof Error ? e.message : _(msg`Upload failed`)
        Toast.show(message, 'error')
      } finally {
        setUploading(false)
      }
    },
    [uploading, disabled, onCoverChange, _],
  )

  const onPress = useCallback(() => {
    if (disabled || uploading) return
    if (IS_WEB) {
      fileInputRef.current?.click()
    } else {
      launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [5, 2],
      }).then(result => {
        if (!result.canceled && result.assets[0]?.uri) {
          pickAndUpload({
            type: 'uri',
            uri: result.assets[0].uri,
            mimeType: result.assets[0].mimeType ?? 'image/jpeg',
          })
        }
      })
    }
  }, [disabled, uploading, pickAndUpload])

  const onWebFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && file.type.startsWith('image/')) {
        pickAndUpload({type: 'file', file})
      }
      e.target.value = ''
    },
    [pickAndUpload],
  )

  const onRemove = useCallback(() => {
    if (disabled) return
    onCoverChange(null)
  }, [disabled, onCoverChange])

  if (coverImageUrl) {
    return (
      <View
        style={[
          a.rounded_md,
          a.overflow_hidden,
          t.atoms.bg_contrast_25,
          {aspectRatio: 5 / 2, position: 'relative'},
        ]}>
        <Image
          source={{uri: coverImageUrl}}
          style={[{width: '100%', height: '100%', resizeMode: 'cover'}]}
          accessibilityLabel={_(msg`Article cover image`)}
        />
        {!disabled && (
          <View
            style={[
              a.absolute,
              {top: 8, right: 8},
              a.flex_row,
              a.gap_xs,
            ]}>
            <Button
              label={_(msg`Change cover`)}
              color="secondary"
              size="tiny"
              onPress={onPress}
              disabled={uploading}>
              <ButtonText>
                <Trans>Change</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Remove cover`)}
              color="secondary"
              size="tiny"
              onPress={onRemove}
              shape="round">
              <RemoveIcon width={18} height={18} />
            </Button>
          </View>
        )}
      </View>
    )
  }

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || uploading}
      style={[
        a.rounded_md,
        a.overflow_hidden,
        t.atoms.bg_contrast_25,
        t.atoms.border_contrast_low,
        {
          aspectRatio: 5 / 2,
          borderWidth: 1,
          borderStyle: 'dashed',
        },
      ]}>
      {IS_WEB && (
        <input
          ref={el => {
            (fileInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
          }}
          type="file"
          accept="image/*"
          onChange={onWebFileChange}
          style={{display: 'none'}}
          aria-hidden
        />
      )}
      <View style={[a.flex_1, a.align_center, a.justify_center, a.p_xl]}>
        <CameraIcon
          width={40}
          height={40}
          style={{color: t.palette.contrast_400}}
          aria-hidden
        />
        <Text
          style={[a.text_sm, a.pt_sm, t.atoms.text_contrast_medium]}
          selectable>
          <Trans>We recommend an image with 5:2 aspect ratio for best results.</Trans>
        </Text>
        {uploading && (
          <Text style={[a.text_xs, a.pt_xs, t.atoms.text_contrast_medium]}>
            <Trans>Uploading…</Trans>
          </Text>
        )}
      </View>
    </Pressable>
  )
}
